import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const card = "bg-white rounded-[16px] p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100";

const Q = [
  ["gp_inicio", "Quando começa a gestão processual?", [
    ["proposta_aceite", "No CPCV marcado", "Quando o consultor marca a data do CPCV (com a proposta aceite), a gestão pede os documentos e prepara o CPCV."],
    ["cpcv_assinado", "No CPCV assinado", "A gestão entra quando o CPCV assinado for carregado."],
    ["sem_gestao", "Não temos gestão processual", "O consultor trata de tudo, como hoje."]]],
  ["gp_atribuicao", "Como chegam os processos a cada gestor?", [
    ["automatica", "Automaticamente", "Vai para o gestor com menos trabalho, ou para o gestor fixo do consultor, se tiver."],
    ["gestor_escolhe", "Cada gestor vai buscar", "Os processos novos ficam numa fila; o gestor carrega em «Fico com este». Em breve.", true]]],
  ["gp_comprador", "Depois da proposta aceite, quem fala com o comprador?", [
    ["gestao", "A gestão processual", "A gestão passa a ser o contacto do comprador: crédito, impostos, dúvidas."],
    ["angariador", "O consultor angariador", "O angariador continua a falar com o comprador; a gestão só trata dos papéis. Em breve.", true],
    ["consultor_comprador", "O consultor do comprador", "Quem trouxe o comprador fala com ele. Se não houver, a gestão. Em breve.", true]]],
];

function Radio({ on, title, text, disabled, onClick, testid }) {
  return (
    <button type="button" disabled={disabled} onClick={onClick} data-testid={testid}
      className={`w-full text-left flex gap-3 rounded-xl border p-4 transition-colors ${on ? "border-slate-900 bg-slate-50" : "border-slate-200 bg-white hover:bg-slate-50"} disabled:opacity-50 disabled:cursor-not-allowed`}>
      <span className={`mt-0.5 w-5 h-5 shrink-0 rounded-full border-2 grid place-items-center ${on ? "border-slate-900" : "border-slate-300"}`}>{on && <span className="w-2.5 h-2.5 rounded-full bg-slate-900" />}</span>
      <span><span className="block text-[15px] font-semibold text-slate-900">{title}</span><span className="block text-sm text-slate-500">{text}</span></span>
    </button>
  );
}

function FixosDialog({ g, consultants, onClose, onDone }) {
  const [sel, setSel] = useState(() => new Set(consultants.filter((c) => c.gestor_fixo_id === g?.id).map((c) => c.id)));
  if (!g) return null;
  const save = async () => {
    try { await api.put(`/agency/gestao/team/${g.id}`, { consultores: [...sel] }); toast.success("Consultores fixos guardados"); onDone(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const flip = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="gp-fixos-dialog">
        <DialogHeader><DialogTitle>Consultores fixos de {g.name}</DialogTitle>
          <DialogDescription>Os processos destes consultores vão sempre para {g.name.split(" ")[0]}, se estiver disponível.</DialogDescription></DialogHeader>
        <div className="space-y-1 max-h-[50vh] overflow-y-auto">
          {consultants.map((c) => (
            <label key={c.id} className="flex items-center gap-3 py-2 text-[15px]" data-testid={`gp-fixo-${c.id}`}>
              <input type="checkbox" checked={sel.has(c.id)} onChange={() => flip(c.id)} className="w-4 h-4" />{c.name}
              {c.gestor_fixo_id && c.gestor_fixo_id !== g.id && <span className="text-xs text-slate-400">(tem outro gestor fixo)</span>}
            </label>
          ))}
        </div>
        <button onClick={save} className="mt-2 h-11 rounded-xl bg-ec-green text-white font-semibold" data-testid="gp-fixos-save">Guardar</button>
      </DialogContent>
    </Dialog>
  );
}

function Team({ data, onDone }) {
  const [edit, setEdit] = useState(null);
  return (
    <div className={card} data-testid="gp-team">
      <h3 className="font-heading text-lg text-ec-green">Equipa de gestão processual</h3>
      {!data.team.length && <p className="text-sm text-slate-500 mt-2">Ainda não há gestores processuais. Convide-os em Equipa, com o cargo «Gestão processual».</p>}
      {data.team.map((g) => (
        <div key={g.id} className="flex items-center gap-4 py-3 border-t border-slate-100 first-of-type:border-t-0" data-testid={`gp-team-${g.id}`}>
          <div className="flex-1 min-w-0">
            <div className="text-[15px] font-semibold text-slate-900">{g.name}{g.ausente_ate && <span className="ml-2 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[11px] font-bold uppercase">De férias até {(() => { const d = new Date(g.ausente_ate); return `${d.getDate()} ${["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"][d.getMonth()]}`; })()}</span>}</div>
            <div className="text-sm text-slate-500" data-testid={`gp-team-load-${g.id}`}>{g.processes} {g.processes === 1 ? "processo" : "processos"} · {g.week} {g.week === 1 ? "CPCV ou escritura" : "CPCVs e escrituras"} esta semana · {g.fixos.length ? `consultores fixos: ${g.fixos.join(", ")}` : "sem consultores fixos"}</div>
          </div>
          <button onClick={() => setEdit(g)} className="h-10 px-4 rounded-xl border border-slate-300 text-sm font-semibold hover:bg-slate-50" data-testid={`gp-team-edit-${g.id}`}>Editar</button>
        </div>
      ))}
      <p className="text-sm text-slate-500 mt-3">A carga = processos ativos + 1 por cada CPCV ou escritura nos próximos 7 dias.</p>
      {edit && <FixosDialog g={edit} consultants={data.consultants} onClose={() => setEdit(null)} onDone={() => { setEdit(null); onDone(); }} />}
    </div>
  );
}

export function GestaoDefinicoes() {
  const [data, setData] = useState(null);
  const [s, setS] = useState(null);
  const load = () => api.get("/agency/gestao").then(({ data }) => { setData(data); setS(data.settings); });
  useEffect(() => { load(); }, []);
  if (!data || !s) return null;
  const save = async () => {
    try { await api.put("/agency/gestao", s); toast.success("Gestão processual guardada"); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const off = s.gp_inicio === "sem_gestao";
  return (
    <div className="grid lg:grid-cols-2 gap-6 items-start">
      <div className={card} data-testid="gp-settings">
        <h3 className="font-heading text-lg text-ec-green">Gestão processual</h3>
        <p className="text-sm text-slate-500 mt-1">Quando e como os processos passam do consultor para a gestão processual.</p>
        {Q.map(([k, title, opts]) => (k === "gp_inicio" || !off) && (
          <div key={k} className="mt-5">
            <div className="text-[15px] font-semibold text-slate-900 mb-2">{title}</div>
            {k === "gp_comprador" && <p className="text-sm text-slate-500 -mt-1 mb-2">Os documentos e o KYC do comprador são sempre da gestão.</p>}
            <div className="space-y-2">
              {opts.map(([v, t, txt, soon]) => <Radio key={v} on={s[k] === v} title={t} text={txt} disabled={soon} onClick={() => setS({ ...s, [k]: v })} testid={`gp-${k}-${v}`} />)}
            </div>
            {k === "gp_atribuicao" && (
              <label className="mt-2 flex gap-3 rounded-xl bg-slate-50 p-4 opacity-60" data-testid="gp-pode-ir-buscar">
                <input type="checkbox" checked={s.gp_pode_ir_buscar} disabled className="mt-1 w-4 h-4" />
                <span><span className="block text-[15px] font-semibold text-slate-900">Os gestores podem sempre ir buscar processos</span>
                  <span className="block text-sm text-slate-500">«Fico com este» num processo sem gestor, ou de um consultor com quem já trabalham. Em breve.</span></span>
              </label>
            )}
          </div>
        ))}
        <p className="text-sm text-slate-500 mt-4">Pode mudar a qualquer momento; só afeta os processos novos.</p>
        <div className="flex justify-end mt-4"><button onClick={save} className="h-11 px-6 rounded-xl bg-ec-green text-white font-semibold" data-testid="gp-save">Guardar</button></div>
      </div>
      {!off && <Team data={data} onDone={load} />}
    </div>
  );
}
